import { FormulaOperator } from '@doss/prisma/types'
import { ParserRuleContext } from 'antlr4'
import {
  checkAggregatedSourceType,
  checkArgsAgainstSpec,
  commonKnownType,
  dataTypeToFormulaValueType,
  filteredKeywordToBaseOperator,
  formatArgumentTypeError,
  formatBranchTypeMismatchError,
  formatNumericLiteralOutOfRangeError,
  FormulaValueType,
  getFormulaOperatorSpec,
  isFormulaNumericLiteralInRange,
  resolveBinaryArithmetic,
  resolveSpecReturnType,
} from '../../utils/formula-operator-specs.js'
import {
  AdditionExprContext,
  ColumnRefContext,
  DivisionExprContext,
  EvenExpressionsContext,
  ExprContext,
  FactorContext,
  FunctionContext,
  IfFunctionContext,
  IfsFunctionContext,
  MultipleArgumentsFunctionContext,
  MultipleExpressionsContext,
  MultiplicationExprContext,
  NegativeNumberContext,
  NestedSumExprContext,
  NowLiteralContext,
  NumberContext,
  SingleArgContext,
  SingleArgumentFunctionContext,
  SingleExpressionContext,
  SingleFactorContext,
  SingleTermContext,
  StringLiteralContext,
  SubtractionExprContext,
  SumExpressionContext,
  ThreeArgumentsFunctionContext,
  ThreeExpressionArgsContext,
  TodayLiteralContext,
  TwoArgumentsFunctionContext,
  TwoExpressionArgsContext,
  TwoExpressionsContext,
} from '../generated/formulaParser.js'
import {
  createErrorToken,
  getTableColumnId,
  type ErrorToken,
  type ParseableColumn,
} from './formula.js'

const operatorFromFunctionText = (text: string): FormulaOperator | undefined => {
  const upperText = text.toUpperCase()
  return Object.values(FormulaOperator).find((operator) => operator === upperText)
}

/**
 * Unwraps a function argument down to its factor when the argument is a single passthrough chain
 * (formulaExpr → sumExpr → term → factor) — used to detect bare literals like the DATEADD unit.
 * Returns undefined when the argument is a compound expression.
 */
const unwrapSingleFactor = (ctx: SingleArgContext): FactorContext | undefined => {
  if (!(ctx instanceof SingleExpressionContext)) {
    return undefined
  }
  const formulaExpr = ctx.formulaExpr()
  if (!(formulaExpr instanceof SumExpressionContext)) {
    return undefined
  }
  const sumExpr = formulaExpr.sumExpr()
  if (!(sumExpr instanceof SingleTermContext)) {
    return undefined
  }
  const term = sumExpr.term()
  if (!(term instanceof SingleFactorContext)) {
    return undefined
  }
  return term.factor()
}

/**
 * Extracts the unquoted text of an argument that is a bare string literal (the DATEADD/DATEDIFF
 * unit), or undefined when the argument is missing or any other expression.
 */
const unitLiteralText = (unitCtx: SingleArgContext | undefined): string | undefined => {
  if (!unitCtx) {
    return undefined
  }
  const factor = unwrapSingleFactor(unitCtx)
  if (!factor || !(factor instanceof StringLiteralContext)) {
    return undefined
  }
  return factor
    .STRING()
    .getText()
    .replace(/^"(.*)"$/, '$1')
}

/**
 * Bottom-up type checker over a parsed formula tree. Resolves the value type of every
 * sub-expression using the operator spec registry and records an ErrorToken anchored on the exact
 * offending sub-expression for each violation. Unknown column references resolve to `undefined`
 * and never error (the parser and reference validation already cover those); an errored
 * sub-expression also resolves to `undefined` so one mistake doesn't cascade into noise.
 */
class FormulaTypeChecker {
  readonly errors: ErrorToken[] = []

  constructor(private readonly columns: ParseableColumn[]) {}

  check(ctx: ParserRuleContext): FormulaValueType | undefined {
    // Root rule — formulaExpr is null when the input only matched invalidInput
    if (ctx instanceof ExprContext) {
      const formulaExpr: unknown = ctx.formulaExpr()
      return formulaExpr instanceof ParserRuleContext ? this.check(formulaExpr) : undefined
    }

    // Passthrough alternatives
    if (ctx instanceof SumExpressionContext) {
      return this.check(ctx.sumExpr())
    }
    if (ctx instanceof SingleTermContext) {
      return this.check(ctx.term())
    }
    if (ctx instanceof SingleFactorContext) {
      return this.check(ctx.factor())
    }
    if (ctx instanceof NestedSumExprContext) {
      return this.check(ctx.sumExpr())
    }
    if (ctx instanceof FunctionContext) {
      return this.check(ctx.functionCall())
    }
    if (ctx instanceof SingleExpressionContext) {
      return this.check(ctx.formulaExpr())
    }

    // Leaves
    if (ctx instanceof NumberContext || ctx instanceof NegativeNumberContext) {
      this.checkNumericLiteralRange(ctx)
      return FormulaValueType.NUMBER
    }
    if (ctx instanceof StringLiteralContext) {
      return FormulaValueType.STRING
    }
    if (ctx instanceof TodayLiteralContext) {
      return FormulaValueType.DATE
    }
    if (ctx instanceof NowLiteralContext) {
      return FormulaValueType.DATE_TIME
    }
    if (ctx instanceof ColumnRefContext) {
      return this.resolveColumnType(ctx)
    }

    // Arithmetic operators
    if (ctx instanceof AdditionExprContext) {
      return this.checkArithmetic(FormulaOperator.ADDITION, ctx.sumExpr(), ctx.term(), ctx)
    }
    if (ctx instanceof SubtractionExprContext) {
      return this.checkArithmetic(FormulaOperator.SUBTRACTION, ctx.sumExpr(), ctx.term(), ctx)
    }
    if (ctx instanceof MultiplicationExprContext) {
      return this.checkArithmetic(FormulaOperator.MULTIPLICATION, ctx.term(), ctx.factor(), ctx)
    }
    if (ctx instanceof DivisionExprContext) {
      return this.checkArithmetic(FormulaOperator.DIVISION, ctx.term(), ctx.factor(), ctx)
    }

    // Function calls
    if (ctx instanceof SingleArgumentFunctionContext) {
      return this.checkFunction(ctx.singleArgFunctionType().getText(), [ctx.singleArg()], ctx)
    }
    if (ctx instanceof TwoArgumentsFunctionContext) {
      const args = ctx.twoArgs()
      const argContexts = args instanceof TwoExpressionArgsContext ? args.singleArg_list() : []
      return this.checkFunction(ctx.twoArgsFunctionType().getText(), argContexts, ctx)
    }
    if (ctx instanceof ThreeArgumentsFunctionContext) {
      const args = ctx.threeArgs()
      const argContexts = args instanceof ThreeExpressionArgsContext ? args.singleArg_list() : []
      return this.checkFunction(ctx.threeArgsFunctionType().getText(), argContexts, ctx)
    }
    if (ctx instanceof MultipleArgumentsFunctionContext) {
      const args = ctx.multipleArgs()
      const argContexts = args instanceof MultipleExpressionsContext ? args.singleArg_list() : []
      return this.checkFunction(ctx.multipleArgsFunctionType().getText(), argContexts, ctx)
    }
    if (ctx instanceof IfFunctionContext) {
      return this.checkIfFunction(ctx)
    }
    if (ctx instanceof IfsFunctionContext) {
      return this.checkIfsFunction(ctx)
    }

    // Anything else (root expr, logic expressions, value lists): type-check any formula
    // sub-expressions nested inside it, but the node itself has no formula value type
    this.checkChildren(ctx)
    return undefined
  }

  private checkChildren(ctx: ParserRuleContext): void {
    for (let i = 0; i < ctx.getChildCount(); i++) {
      const child = ctx.getChild(i)
      if (child instanceof ParserRuleContext) {
        this.check(child)
      }
    }
  }

  private resolveColumnType(ctx: ColumnRefContext): FormulaValueType | undefined {
    const columnId = getTableColumnId(ctx)
    const column = this.columns.find((col) => col.id === columnId)
    if (!column) {
      return undefined
    }
    return dataTypeToFormulaValueType(column.type, column.lookupColumn?.type ?? null)
  }

  private checkArithmetic(
    operator: FormulaOperator,
    leftCtx: ParserRuleContext,
    rightCtx: ParserRuleContext,
    fullCtx: ParserRuleContext,
  ): FormulaValueType | undefined {
    const left = this.check(leftCtx)
    const right = this.check(rightCtx)
    const result = resolveBinaryArithmetic(operator, left, right)
    if (result.error) {
      this.errors.push(createErrorToken(fullCtx, result.error))
      return undefined
    }
    return result.type
  }

  private checkFunction(
    functionText: string,
    argContexts: SingleArgContext[],
    fullCtx: ParserRuleContext,
  ): FormulaValueType | undefined {
    const operator = operatorFromFunctionText(functionText)
    const spec = operator ? getFormulaOperatorSpec(operator) : undefined
    const argTypes = argContexts.map((argCtx) => this.check(argCtx))
    if (!operator || !spec) {
      return undefined
    }

    const { errors: argErrors, resultType } = checkArgsAgainstSpec(spec, argTypes, (index) =>
      unitLiteralText(argContexts[index]),
    )
    argErrors.forEach((argError) => {
      const anchor =
        argError.argIndex !== undefined ? (argContexts[argError.argIndex] ?? fullCtx) : fullCtx
      this.errors.push(createErrorToken(anchor, argError.message))
    })
    return resultType
  }

  private checkIfFunction(ctx: IfFunctionContext): FormulaValueType | undefined {
    const ifArgs = ctx.ifArgs()
    if (!(ifArgs instanceof TwoExpressionsContext)) {
      return FormulaValueType.NUMBER
    }
    // The condition can embed formulas
    this.checkChildren(ifArgs.logicExpr())

    const functionText = ctx.ifFunctionType().getText().toUpperCase()
    const baseOperator = filteredKeywordToBaseOperator[functionText]
    const baseSpec = baseOperator ? getFormulaOperatorSpec(baseOperator) : undefined
    const valueType = this.check(ifArgs.formulaExpr())
    // Filtered aggregations admit the same source types as their column-level form (COUNTIF
    // accepts anything). Unknown value types stay lenient here
    if (baseOperator !== undefined && valueType !== undefined) {
      const violatedTypes = checkAggregatedSourceType(baseOperator, valueType)
      if (violatedTypes) {
        this.errors.push(
          createErrorToken(
            ifArgs.formulaExpr(),
            formatArgumentTypeError(
              baseSpec?.filteredKeyword ?? baseOperator,
              1,
              violatedTypes,
              valueType,
            ),
          ),
        )
        return undefined
      }
    }
    // MINIFS/MAXIFS over a date column return that date type; SUMIF/COUNTIF stay numeric
    return baseSpec ? resolveSpecReturnType(baseSpec, [valueType]) : FormulaValueType.NUMBER
  }

  /** Every IFS branch result must resolve to the same type, which becomes the IFS result type */
  private checkIfsFunction(ctx: IfsFunctionContext): FormulaValueType | undefined {
    const ifsArgs = ctx.ifsArgs()
    if (!(ifsArgs instanceof EvenExpressionsContext)) {
      return undefined
    }
    const branchTypes: Array<FormulaValueType | undefined> = []
    for (const branch of ifsArgs.ifArgs_list()) {
      if (!(branch instanceof TwoExpressionsContext)) {
        continue
      }
      this.checkChildren(branch.logicExpr())
      branchTypes.push(this.check(branch.formulaExpr()))
    }
    const { type, mismatch } = commonKnownType(branchTypes)
    if (mismatch) {
      this.errors.push(createErrorToken(ctx, formatBranchTypeMismatchError(mismatch)))
    }
    return type
  }

  private checkNumericLiteralRange(ctx: NumberContext | NegativeNumberContext): void {
    const literal = ctx.getText()
    if (!isFormulaNumericLiteralInRange(literal)) {
      this.errors.push(createErrorToken(ctx, formatNumericLiteralOutOfRangeError(literal)))
    }
  }
}

/**
 * Type-checks the formula sub-expressions of a parsed tree against the operator spec registry.
 * Accepts either parse root: a formula tree ('formula' scope) or a logic-expression tree
 * ('logicalExpression' scope), where formulas embedded as condition operands are checked.
 * Returns the resolved output type (undefined when unknown) and an ErrorToken per type violation
 */
export function typeCheckFormulaTree(
  tree: ParserRuleContext,
  columns: ParseableColumn[],
): { errors: ErrorToken[]; outputType: FormulaValueType | undefined } {
  const checker = new FormulaTypeChecker(columns)
  const outputType = checker.check(tree)
  return { errors: checker.errors, outputType }
}
